import { t } from "i18next";
import { isNotNilOrEmpty, isTruthy } from "ramda-adjunct";
import * as React from "react";
import { ReactNode, useCallback } from "react";
import { match } from "ts-pattern";
import { JobName } from "@sinch/components/application/Job/components/JobName/JobName";
import { OrderName } from "@sinch/components/application/Order/components/OrderName/OrderName";
import { PositionName } from "@sinch/components/application/Position/components/PositionName/PositionName";
import { LabelGetter } from "@sinch/components/application/RatesCompositionTable/EntityOverviewTable/rowBuilders";
import {
  AttendanceTableStructureFragment,
  JobTableStructureFragment,
  OrderTableStructureFragment,
  PositionTableStructureFragment,
  ShiftTableStructureFragment,
} from "@sinch/components/application/RatesCompositionTable/queries.types";
import { ShiftName } from "@sinch/components/application/Shift/components/ShiftName/ShiftName";
import { WorkerListItem } from "@sinch/components/application/Worker/WorkerListItem";
import { prop } from "ramda";
import { Stack } from "@mui/material";
import {
  OverrideIcon,
  RateComponentComposition,
} from "@sinch/components/application/RatesCompositionTable/InternalRateComponentsMarginTable";
import { Ratelist } from "@sinch/types/sinch.types";
import { RatelistType } from "@sinch/core/entities/serverEnums.types";

export const useLabelGetter = (): LabelGetter =>
  useCallback(
    (data, entityName) =>
      match(entityName)
        .returnType<ReactNode>()
        .with("Order", () => {
          const { name, number } = data as unknown as OrderTableStructureFragment;
          return <OrderName id={number} name={name} />;
        })
        .with("Job", () => {
          const { id, name } = data as unknown as JobTableStructureFragment;
          return <JobName id={id} name={name} />;
        })
        .with("Shift", () => {
          const { id, name, beginning, end } = data as unknown as ShiftTableStructureFragment;
          return <ShiftName beginning={beginning} end={end} id={id} name={name} />;
        })
        .with("Position", () => {
          const { name, size, role, id, beginning, end, attendances, profession, priceRatelist, wageRatelist, shift } =
            data as unknown as PositionTableStructureFragment;
          return (
            <Stack alignItems="center" direction="row" gap={0.5}>
              <PositionName
                beginning={beginning}
                capacity={size}
                end={end}
                id={id}
                name={[profession.name, name].filter(isNotNilOrEmpty).join(" - ")}
                role={role}
                staffCount={attendances.length}
              />
              <OverrideIcon
                priceOverrideLabel={getOverriddenRatelistLabel(
                  priceRatelist,
                  shift.job.priceRatelist,
                  t("Ratelist.hasOverriddenPricelist")
                )}
                wageOverrideLabel={getOverriddenRatelistLabel(
                  wageRatelist,
                  shift.job.wageRatelist,
                  t("Ratelist.hasOverriddenWagelist")
                )}
              />
            </Stack>
          );
        })
        .with("Attendance", () => {
          const overriddenRatelistIds = new Map(
            ((data.overriddenRatelists ?? []) as Pick<Ratelist, "id" | "name" | "type">[]).map(({ id, ...rest }) => [
              id,
              rest,
            ])
          );

          const priceRatelists = getOverriddenRatelists(
            data.priceComposition as RateComponentComposition | undefined,
            overriddenRatelistIds
          );
          const wageRatelists = getOverriddenRatelists(
            data.wageComposition as RateComponentComposition | undefined,
            overriddenRatelistIds
          );

          const { worker, rating } = data as unknown as AttendanceTableStructureFragment;
          if (!worker) {
            return <WorkerListItem hideRating worker={{ name: t("Shift.emptyPosition") }} />;
          }
          return (
            <Stack alignItems="center" direction="row" gap={0.5}>
              <WorkerListItem expectedRating={rating?.amount ?? worker.workerinfo?.rating ?? 0} worker={worker} />
              <OverrideIcon
                priceOverrideLabel={isTruthy(priceRatelists) && t("Ratelist.hasOverriddenPricelist")}
                wageOverrideLabel={isTruthy(wageRatelists) && t("Ratelist.hasOverriddenWagelist")}
              />
            </Stack>
          );
        })
        .exhaustive(),
    []
  );

/**
 * Tooltip label for a ratelist that overrides the one inherited from the parent entity,
 * `false` when it does not override it.
 */
const getOverriddenRatelistLabel = (
  ownRatelist: Pick<Ratelist, "id" | "name" | "type" | "adHoc"> | null | undefined,
  parentRatelist: Pick<Ratelist, "id"> | null | undefined,
  prefix: string
): string | false => {
  if (ownRatelist == null || ownRatelist.id === parentRatelist?.id) {
    return false;
  }
  const ratelistName = ownRatelist.adHoc ? t("Ratelist.adHoc") : ownRatelist.name;

  return `${prefix}: ${ratelistName}`;
};

const getOverriddenRatelists = (
  composition: RateComponentComposition | undefined,
  overriddenRatelistIds: Map<
    string,
    {
      name: string;
      type: RatelistType;
    }
  >
) =>
  composition?.components.some(
    (item) => prop("ratelistId", item) != null && overriddenRatelistIds.get(item.ratelistId!)
  );
